import type { ComponentType } from 'react';
import { Bot, Building2, CloudCog } from 'lucide-react';
import type { CredentialId } from '@/content/credentials';

/**
 * Single source of truth for the page's one mental model, which is the
 * centerpiece image read out loud:
 *
 *   image layer               site name            what it is
 *   Application & Agents   →  What you use         the apps
 *   Inference & Optimization → What makes it fast  the engine
 *   Compute Abstraction    →  Where it runs        the three pillars live here
 *
 * The pillars (Local, Hybrid, Physical) are WHERE the system runs — the bottom
 * row. The stacks (Qwen + Alibaba Cloud, NVIDIA) are WHO supplies the parts.
 * There is no fourth model: the old 7-layer hero and 6-layer platform ledger
 * were deleted with this file's arrival, partly because three layer models on
 * one page was itself the confusion.
 *
 * Wording: plain English in the sentence, the image's own jargon only in the
 * `terms` lists, which render as a quiet mono line. Capability claims say
 * "can" until the owner confirms a delivered project (docs/HANDOFF.md, owner
 * inputs).
 */

type Icon = ComponentType<{ className?: string }>;

// ─── Pillars ───────────────────────────────────────────────────────────────

export type PillarId = 'local' | 'hybrid' | 'physical';

export interface Pillar {
  id: PillarId;
  name: string;
  title: string;
  line: string;
  goodFor: string;
  icon: Icon;
}

export const PILLARS: readonly Pillar[] = [
  {
    id: 'local',
    name: 'Local AI',
    title: 'AI inside your building.',
    line: 'Runs on a machine in your office. Your data never leaves the building.',
    goodFor: 'Document Q&A, HR and sales assistants, LINE chatbots',
    icon: Building2,
  },
  {
    id: 'hybrid',
    name: 'Hybrid AI',
    title: 'Private first. Cloud when you choose.',
    line: 'Sensitive work stays on your machine. We can set up very large or very busy jobs to run on Alibaba Cloud, under rules you set.',
    goodFor: 'Busy seasons, very large models, many branches',
    icon: CloudCog,
  },
  {
    id: 'physical',
    name: 'Physical AI',
    title: 'AI inside machines and robots.',
    line: 'AI that sees and acts in the real world: robot arms, cameras and drones. It can learn in simulation first, then run on the device.',
    goodFor: 'Factories, labs, universities, robot kits for teaching',
    icon: Bot,
  },
];

// ─── The image's six benefits, each placed in the layer that delivers it ─────

export type BenefitId =
  | 'multi-modal'
  | 'model-flexibility'
  | 'low-latency'
  | 'efficient-compute'
  | 'privacy-first'
  | 'production-ready';

export interface Benefit {
  id: BenefitId;
  label: string;
  gloss: string;
}

export const BENEFITS: Readonly<Record<BenefitId, Benefit>> = {
  'multi-modal': { id: 'multi-modal', label: 'Multi-Modal', gloss: 'text, images, audio, video and documents' },
  'model-flexibility': { id: 'model-flexibility', label: 'Model Flexibility', gloss: 'small models for quick jobs, large ones for hard ones' },
  'low-latency': { id: 'low-latency', label: 'Low Latency', gloss: 'replies start fast, even when many people use it' },
  'efficient-compute': { id: 'efficient-compute', label: 'Efficient Compute', gloss: 'more work from every GPU' },
  'privacy-first': { id: 'privacy-first', label: 'Privacy First', gloss: 'on-device, on-premise or hybrid: you decide' },
  'production-ready': { id: 'production-ready', label: 'Production Ready', gloss: 'we install it, tune it and support it' },
};

// ─── The three layers ──────────────────────────────────────────────────────

export type LayerId = 'apps' | 'engine' | 'compute';

export interface Layer {
  id: LayerId;
  n: string;
  /** Plain-English name used on the site. */
  name: string;
  /** The image's own label for this layer. */
  imageName: string;
  line: string;
  /** The image's jargon for this layer, rendered as one mono line. */
  terms: readonly string[];
  gets: readonly BenefitId[];
}

export const LAYERS: readonly Layer[] = [
  {
    id: 'apps',
    n: '01',
    name: 'What you use',
    imageName: 'Application & Agents',
    line: 'Chat assistants, document search that shows its sources, web and LINE chat, and agents that finish tasks for you.',
    terms: ['LLM text', 'vision', 'audio', 'video', 'document RAG', 'multi-modal fusion', 'tool use', 'function calling', 'domain apps', 'hybrid workloads'],
    gets: ['multi-modal', 'model-flexibility'],
  },
  {
    id: 'engine',
    n: '02',
    name: 'What makes it fast',
    imageName: 'Inference & Optimization',
    line: 'The model drafts several words ahead and checks them in one step, so replies come faster with the same quality, and one machine serves many people at once.',
    terms: ['speculative decoding (draft model or built-in MTP)', 'continuous batching', 'KV cache', 'paged attention', 'quantization', 'dynamic routing', 'TTFT reduction', 'high throughput'],
    gets: ['low-latency', 'efficient-compute'],
  },
  {
    id: 'compute',
    n: '03',
    name: 'Where it runs',
    imageName: 'Compute Abstraction Layer',
    line: 'The same system runs on whatever hardware fits the job, mixing local machines with the cloud when you want it to.',
    terms: ['CPU', 'GPU', 'NPU / accelerator', 'edge / on-prem', 'hybrid / cloud', 'heterogeneous hardware', 'local + hybrid execution'],
    gets: ['privacy-first', 'production-ready'],
  },
];

// ─── The two stacks ────────────────────────────────────────────────────────

export type StackId = 'qwen' | 'nvidia';

export interface Stack {
  id: StackId;
  name: string;
  role: string;
  line: string;
  terms: readonly string[];
  credentials: readonly CredentialId[];
}

export const STACKS: readonly Stack[] = [
  {
    id: 'qwen',
    name: 'Qwen + Alibaba Cloud',
    role: 'The models',
    line: 'Open-weight AI models your company can run and keep. The Qwen family lists Thai among its languages; newer Qwen models also read images and answer faster with built-in Multi-Token Prediction. For Hybrid AI, Alibaba Cloud can host the bigger jobs, including in its Bangkok region.',
    terms: ['Qwen open-weight models', 'Qwen-Agent · MCP tools', 'Alibaba Cloud PAI-EAS (Bangkok)', 'Alibaba Cloud Model Studio'],
    credentials: ['alibaba-partner', 'qwen-ambassador'],
  },
  {
    id: 'nvidia',
    name: 'NVIDIA',
    role: 'The computers',
    line: 'The hardware and the speed software. DGX Spark is a desktop AI computer that runs large models privately. Jetson puts AI inside robots and machines, and Isaac lets robots learn in simulation first.',
    terms: ['DGX Spark (GB10 Grace Blackwell)', 'Jetson', 'TensorRT-LLM', 'NVFP4', 'Isaac'],
    credentials: ['nvidia-inception'],
  },
];

// ─── The centerpiece image ─────────────────────────────────────────────────

export const HERO_ALT =
  'The AGICAFET AI Inference Layer: three stacked layers, with apps and agents on top, the inference engine in the middle and the computers it runs on at the bottom.';
